import {
  Copy,
  ExternalLink,
  Linkedin,
  Loader2,
  Mail,
  MapPin,
  Phone,
} from "lucide-react";
import { toast } from "sonner";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { StackDrawer } from "@/components/ui/stack-drawer";

import { Lead, LEAD_STATUS_ORDER,LeadSearch, LeadStatus } from "../types/leads";

// --- Helpers ---

const STATUS_CONFIG: Record<LeadStatus, { label: string; dot: string }> = {
  new: { label: "New", dot: "bg-sds-blue-100" },
  contacted: { label: "Contacted", dot: "bg-sds-yellow-100" },
  in_conversation: { label: "In Conversation", dot: "bg-sds-purple-100" },
  qualified: { label: "Qualified", dot: "bg-[#34d399]" },
  converted: { label: "Converted", dot: "bg-sds-green-100" },
  deleted: { label: "Deleted", dot: "bg-sds-red-100" },
};

const getSearch = (searchId: string, searches: LeadSearch[]) =>
  searches.find((s) => s.id === searchId);

const getSearchLabel = (searchId: string, searches: LeadSearch[]) => {
  const search = getSearch(searchId, searches);
  if (!search) return "Unknown";
  const index = searches.findIndex((s) => s.id === searchId);
  return `Search #${searches.length - index}`;
};

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });

const copyToClipboard = (text: string, e: React.MouseEvent) => {
  e.preventDefault();
  e.stopPropagation();
  navigator.clipboard.writeText(text);
  toast.success("Copied!", { description: text });
};

// --- Component ---

interface LeadDetailDrawerProps {
  lead: Lead | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  searches: LeadSearch[];
  onUpdateStatus: (leadId: string, status: LeadStatus) => void;
  isUpdatingStatus?: boolean;
}

const LeadDetailDrawer = ({
  lead,
  open,
  onOpenChange,
  searches,
  onUpdateStatus,
  isUpdatingStatus,
}: LeadDetailDrawerProps) => {

  if (!lead) return null;

  const search = getSearch(lead.searchId, searches);

  return (
    <StackDrawer
      open={open}
      onOpenChange={onOpenChange}
      title={lead.contactName}
      subtitle={`${lead.contactTitle} · ${lead.companyName}`}
    >
      <div className="space-y-4 sm:space-y-6">
        {/* Status selector */}
        <div className="flex items-center gap-2">
          <Select
            value={lead.status}
            disabled={isUpdatingStatus}
            onValueChange={(value: LeadStatus) =>
              onUpdateStatus(lead.id, value)
            }
          >
            <SelectTrigger className="h-10 sm:h-8 w-auto min-w-[140px] border border-[#e8e8e8] shadow-none bg-transparent px-3 sm:px-2.5 rounded-md text-[13px] sm:text-[12px] font-semibold text-foreground focus:ring-0 focus:border-[#e8e8e8] data-[state=open]:border-[#e8e8e8] [&>svg]:h-3.5 [&>svg]:w-3.5 sm:[&>svg]:h-3 sm:[&>svg]:w-3 [&>svg]:text-[#a4a4a4] disabled:opacity-60">
              <SelectValue />
            </SelectTrigger>
            <SelectContent align="start" className="min-w-[160px] border-[#e8e8e8] data-[state=open]:border-[#e8e8e8]">
              {LEAD_STATUS_ORDER.map((status) => {
                const opt = STATUS_CONFIG[status];
                return (
                  <SelectItem key={status} value={status} className="text-[11px] font-semibold text-[#494949]">
                    <span className="flex items-center gap-1.5">
                      <span className={`h-2 w-2 rounded-full shrink-0 ${opt.dot}`} />
                      {opt.label}
                    </span>
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
          {isUpdatingStatus && (
            <Loader2 className="h-3.5 w-3.5 text-[#a4a4a4] animate-spin" />
          )}
        </div>

        {/* Contact card */}
        <div className="rounded-lg border border-[#e8e8e8] p-4 space-y-3">
          <span className="text-[10px] font-semibold tracking-wide uppercase text-muted-foreground">
            Contact
          </span>
          <div className="space-y-1 sm:space-y-2">
            {/* Email */}
            <a
              href={`mailto:${lead.contactEmail}`}
              className="group flex items-center gap-2.5 sm:gap-2 py-2 sm:py-0.5 text-sm text-foreground hover:text-sds-purple-100 transition-colors"
            >
              <Mail className="h-4 w-4 sm:h-3.5 sm:w-3.5 text-muted-foreground group-hover:text-sds-purple-100 shrink-0" />
              <span className="truncate flex-1">{lead.contactEmail}</span>
              <button
                type="button"
                className="shrink-0 p-1.5 sm:p-0 rounded-md sm:rounded-none bg-[#f5f5f5] sm:bg-transparent opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity"
                onClick={(e) => copyToClipboard(lead.contactEmail, e)}
              >
                <Copy className="h-3.5 w-3.5 sm:h-3 sm:w-3 text-muted-foreground" />
              </button>
            </a>

            {/* Phone */}
            {lead.contactPhone && (
              <a
                href={`tel:${lead.contactPhone}`}
                className="group flex items-center gap-2.5 sm:gap-2 py-2 sm:py-0.5 text-sm text-foreground hover:text-sds-purple-100 transition-colors"
              >
                <Phone className="h-4 w-4 sm:h-3.5 sm:w-3.5 text-muted-foreground group-hover:text-sds-purple-100 shrink-0" />
                <span className="truncate flex-1">{lead.contactPhone}</span>
                <button
                  type="button"
                  className="shrink-0 p-1.5 sm:p-0 rounded-md sm:rounded-none bg-[#f5f5f5] sm:bg-transparent opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity"
                  onClick={(e) => copyToClipboard(lead.contactPhone!, e)}
                >
                  <Copy className="h-3.5 w-3.5 sm:h-3 sm:w-3 text-muted-foreground" />
                </button>
              </a>
            )}

            {/* LinkedIn */}
            <a
              href={lead.linkedinUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="group flex items-center gap-2.5 sm:gap-2 py-2 sm:py-0.5 text-sm text-foreground hover:text-sds-purple-100 transition-colors"
            >
              <Linkedin className="h-4 w-4 sm:h-3.5 sm:w-3.5 text-muted-foreground group-hover:text-sds-purple-100 shrink-0" />
              <span className="truncate flex-1">{lead.linkedinUrl.replace("https://linkedin.com/", "")}</span>
              <ExternalLink className="h-3.5 w-3.5 sm:h-3 sm:w-3 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity text-muted-foreground shrink-0" />
            </a>

            {/* Location */}
            {lead.location && (
              <div className="flex items-center gap-2.5 sm:gap-2 py-2 sm:py-0.5 text-sm text-muted-foreground">
                <MapPin className="h-4 w-4 sm:h-3.5 sm:w-3.5 shrink-0" />
                {lead.location}
              </div>
            )}

            {/* Company URL */}
            <a
              href={lead.companyUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="group flex items-center gap-2.5 sm:gap-2 py-2 sm:py-0.5 text-sm text-foreground hover:text-sds-purple-100 transition-colors"
            >
              <ExternalLink className="h-4 w-4 sm:h-3.5 sm:w-3.5 text-muted-foreground group-hover:text-sds-purple-100 shrink-0" />
              <span className="truncate flex-1">{lead.companyUrl.replace("https://", "")}</span>
            </a>
          </div>
        </div>

        {/* Context card */}
        <div className="rounded-lg border border-[#e8e8e8] p-4 space-y-3">
          <span className="text-[10px] font-semibold tracking-wide uppercase text-muted-foreground">
            Context
          </span>
          <p className="text-sm text-foreground leading-relaxed">
            {lead.companyDescription}
          </p>
          {search && (
            <blockquote className="border-l-2 border-sds-purple-40 pl-3 py-1">
              <p className="text-xs text-muted-foreground italic leading-relaxed">
                {search.query.length > 150
                  ? `${search.query.slice(0, 150)}...`
                  : search.query}
              </p>
              <p className="text-[10px] text-muted-foreground mt-1">
                {getSearchLabel(lead.searchId, searches)} · {formatDate(lead.createdAt)}
              </p>
            </blockquote>
          )}
        </div>
      </div>
    </StackDrawer>
  );
};

export default LeadDetailDrawer;
